import React from "react";
import * as yup from "yup";
import { ModalForm, useModalDispatch } from "collections/Modal";
import { useTranslation } from "utilities/i18n";
import { useCreateTenantMutation } from "generated/graphql";
import { DISMISS, Input, useNotification } from "collections";
import { CancelButton, SubmitButton } from "elements";
import { Form } from "collections/Form/Form";

export const AddAccountFormSchema = yup.object().shape({
  accountName: yup
    .string()
    .label("Account Name")
    .required()
});
type AddAccountFormTypes = yup.InferType<typeof AddAccountFormSchema>;

const AddAccountModal: React.FC<{
  onComplete: () => void;
}> = ({ onComplete }) => {
  const { t } = useTranslation();
  const [addNotification] = useNotification();
  const returnTo = useModalDispatch();
  const [, createTenant] = useCreateTenantMutation();

  const onSubmit = async (values: AddAccountFormTypes) => {
    const { data, error } = await createTenant({
      input: { name: values.accountName }
    });

    if (error || !data?.createTenant.tenant) {
      addNotification({
        type: "error",
        message: t("ACCOUNT_ADD_ERROR", { name: values.accountName }),
        dismiss: DISMISS.USER_ONLY,
        persistThroughNav: true
      });
    } else {
      addNotification({
        type: "success",
        message: t("ACCOUNT_ADD_SUCCESS", { name: values.accountName }),
        dismiss: DISMISS.DEFAULT,
        persistThroughNav: true
      });
    }

    returnTo();
    onComplete();
  };

  return (
    <ModalForm
      name="AddAccount"
      title={t("ACCOUNT_ADD_TITLE")}
      validationSchema={AddAccountFormSchema}
      initialValues={{ accountName: "" }}
      onSubmit={onSubmit}
    >
      {({ values, isSubmitting }) => {
        return (
          <Form autoComplete="off" data-testid="add-account-modal">
            <div className="mt-1 mh-4">
              <Input
                name="accountName"
                placeholder="Account Name"
                type="text"
              />
            </div>
            <div className="btn-container">
              <CancelButton onClick={returnTo} disabled={isSubmitting} />
              <SubmitButton
                isSubmitting={isSubmitting}
                disabled={!values.accountName}
                labelKey="CONFIRM"
                submittingLabelKey="SAVING"
              />
            </div>
          </Form>
        );
      }}
    </ModalForm>
  );
};

export default AddAccountModal;
